import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { toast } from 'src/components/ui/toast';
import {
  useSetDraftFreight,
  type DraftPurchaseOrderFreight,
  type FreightOptionRow,
} from 'src/api/rpc/hooks/procurement/useFreightOptions';
import { termsKindLabel } from 'src/pages/authenticated/procurement/freight-options/freightOptionLabels';
import { draftFreightSaveInput, pickedOptionStart } from './draftFreightFormat';
import { ShippingInstructionsField } from './ShippingInstructionsField';

/** Picks one of the branch's freight options for a draft PO. */
export function DraftFreightEditor({
  freight,
  options,
  onDone,
}: {
  freight: DraftPurchaseOrderFreight;
  options: FreightOptionRow[];
  onDone: () => void;
}) {
  const gt = useGT();
  const setFreight = useSetDraftFreight();
  const [optionId, setOptionId] = useState(
    freight.freightOptionId ?? freight.suggestedOptionId ?? '',
  );
  const picked = options.find((option) => option.id === optionId);
  // The draft's own account for its saved option, so a one-off survives.
  const keepsSavedOption = picked?.id === freight.freightOptionId;
  const [carrierAccount, setCarrierAccount] = useState(
    (keepsSavedOption
      ? freight.carrierAccount
      : (picked?.carrierAccount ?? freight.carrierAccount)) ?? '',
  );
  const [instructions, setInstructions] = useState(
    keepsSavedOption ? freight.instructions : (picked?.instructions ?? []),
  );

  function pick(id: string) {
    const start = pickedOptionStart(options, id);
    setOptionId(id);
    setCarrierAccount(start.carrierAccount);
    setInstructions(start.instructions);
  }

  function save() {
    if (!picked) return;
    setFreight.mutate(
      draftFreightSaveInput({
        freight,
        option: picked,
        carrierAccount,
        instructions,
      }),
      {
        onSuccess: () => {
          toast.success(
            gt('Freight updated.', {
              context: 'toast after changing a draft PO freight',
            }),
          );
          onDone();
        },
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <div className="grid gap-2">
      <FreightOptionFields
        idPrefix="draft-freight"
        options={options}
        suggestedOptionId={freight.suggestedOptionId}
        optionId={optionId}
        carrierAccount={carrierAccount}
        instructions={instructions}
        onPick={pick}
        onCarrierAccount={setCarrierAccount}
        onInstructions={setInstructions}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onDone}>
          <T context="dialog button">Cancel</T>
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!picked || setFreight.isPending}
          onClick={save}
        >
          <T context="dialog button">Save</T>
        </Button>
      </div>
    </div>
  );
}

/**
 * The freight option picker and carrier account, for a draft's Freight panel
 * and the release dialog.
 */
export function FreightOptionFields({
  idPrefix,
  options,
  suggestedOptionId,
  optionId,
  carrierAccount,
  instructions,
  onPick,
  onCarrierAccount,
  onInstructions,
}: {
  idPrefix: string;
  options: FreightOptionRow[];
  suggestedOptionId: string | null;
  optionId: string;
  carrierAccount: string;
  instructions: string[];
  onPick: (optionId: string) => void;
  onCarrierAccount: (carrierAccount: string) => void;
  onInstructions: (lines: string[]) => void;
}) {
  const gt = useGT();
  return (
    <>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-option`}>
          <T context="draft PO freight picker label">Freight option</T>
        </Label>
        <Select value={optionId} onValueChange={(value) => onPick(value ?? '')}>
          <SelectTrigger id={`${idPrefix}-option`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {optionLabel(
                  option,
                  termsKindLabel(gt, option.freightTermsKind),
                )}
                {option.id === suggestedOptionId
                  ? ` · ${gt('suggested', {
                      context:
                        'freight option list marker: picked by the branch rules',
                    })}`
                  : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-account`}>
          <T context="draft PO freight field">Carrier account</T>
        </Label>
        <Input
          id={`${idPrefix}-account`}
          value={carrierAccount}
          maxLength={64}
          onChange={(event) => onCarrierAccount(event.target.value)}
        />
      </div>
      <ShippingInstructionsField
        // A new option starts from its own saved instructions.
        key={optionId}
        idPrefix={idPrefix}
        optionId={optionId || null}
        value={instructions}
        onChange={onInstructions}
      />
    </>
  );
}

/** "PL PUROLATOR · Collect · 3707989". */
function optionLabel(option: FreightOptionRow, kindLabel: string): string {
  return [
    [option.shipViaCode, option.shipViaDescription].filter(Boolean).join(' '),
    kindLabel,
    option.carrierAccount,
  ]
    .filter(Boolean)
    .join(' · ');
}
